<script setup lang="ts">
import { TRANSITION_SOURCE } from '#shared/constant/constant'
import {
  FUELS,
  OTHER_OPTIONS,
  CAR_PRICE,
  CAR_LENGTH,
  CAR_HEIGHT,
  CAR_WIDTH,
  CAR_WEIGHT,
} from '@/constant/conditionParams'
import type {
  CheckedParamText,
  IdName,
  StringMinMax,
} from '@/interfaces/global'
import type { SendParams } from '@/interfaces/searchParam'
import type { CarModelContents } from '#shared/interfaces/car-model-list/model'
import _ from 'lodash'

defineExpose({
  clearSendData,
  initialSet,
})

interface groupButtonSelected {
  groupNewCar: number // 現行販売車
  groupForeignCar: number // 国産車・輸入車
  groupDriveSystem: number // 駆動
  groupMission: number // ミッション
}
const timer: Ref<ReturnType<typeof setTimeout> | undefined> = ref(undefined)
const nuxtApp = useNuxtApp()
const fuelCategories: Ref<string[]> = useState('fuel_categories', () => {
  return []
})
// $getBrands / $getBodyTypes は plugin が provide する関数なので中身は $fetch。
// setup 直下からの呼び出しはハイドレーション後に再実行されるため、
// useAsyncData のハンドラ内にまとめて payload 転送を効かせる（あわせて2本を並列化）
const { data: conditionMasters } = await useAsyncData(
  'condition-find-masters',
  () => Promise.all([nuxtApp.$getBrands(), nuxtApp.$getBodyTypes()])
)
const brandList: IdName[] = conditionMasters.value?.[0] ?? []
const brandName: Ref<string> = ref('指定なし')
const bodyTypeList: IdName[] = conditionMasters.value?.[1] ?? []
const bodyTypeName: Ref<string> = ref('指定なし')
const tagMakerFlg = ref(true)
const makerLimitFlg = useState('maker_limit')
const checkList: Ref<number[]> = useState('brand_checked_list')
const tempSendParams: Ref<SendParams> = useState('temp_send_params', () => {
  return nuxtApp.$createSearchParams()
})
const carModelList: Ref<CarModelContents> = useState('car_model_list')
const carCount: Ref<number> = ref(0)
const newCar1 = ref<HTMLInputElement | null>(null)
const newCar2 = ref<HTMLInputElement | null>(null)
const foreignCar1 = ref<HTMLInputElement | null>(null)
const foreignCar2 = ref<HTMLInputElement | null>(null)
const foreignCar3 = ref<HTMLInputElement | null>(null)
const driveSystem1 = ref<HTMLInputElement | null>(null)
const driveSystem2 = ref<HTMLInputElement | null>(null)
const driveSystem3 = ref<HTMLInputElement | null>(null)
const mission1 = ref<HTMLInputElement | null>(null)
const mission2 = ref<HTMLInputElement | null>(null)
const mission3 = ref<HTMLInputElement | null>(null)
const clickTag = ref(false)
const bodyTypeLength = useState('body_type_length', () => 0)
const allSelectFlgMap = useState('all_select_flg_map', () => new Map())
const holdAllSelectFlgMap = useState('hold_all_select_flg_map', () => new Map())
const catalogSearchMakerFlg: Ref<boolean> = useState(
  'catalog_search_maker_flg',
  () => true
)
const catalogSearchBodyTypeFlg: Ref<boolean> = useState(
  'catalog_search_body_type_flg',
  () => true
)
const bodyTypeCheckList = useState<Array<Array<string>>>(
  'body_types_check_list',
  () => [[]]
)

const carLengthCheckList: Ref<StringMinMax[]> = useState(
  'car_length_check_list',
  () => {
    return []
  }
)
const carWidthCheckList: Ref<StringMinMax[]> = useState(
  'car_width_check_list',
  () => {
    return []
  }
)
const carHeightCheckList: Ref<StringMinMax[]> = useState(
  'car_height_check_list',
  () => {
    return []
  }
)
const carWeightCheckList: Ref<StringMinMax[]> = useState(
  'car_weight_check_list',
  () => {
    return []
  }
)
const carLengthInputList: Ref<StringMinMax> = useState(
  'car_length_input_list',
  () => {
    return {}
  }
)
const carWidthInputList: Ref<StringMinMax> = useState(
  'car_width_input_list',
  () => {
    return {}
  }
)
const carHeightInputList: Ref<StringMinMax> = useState(
  'car_height_input_list',
  () => {
    return {}
  }
)
const carWeightInputList: Ref<StringMinMax> = useState(
  'car_weight_input_list',
  () => {
    return {}
  }
)
const groupConditionsSelected: Ref<groupButtonSelected> = useState(
  'group_button_selected',
  () => {
    return {
      groupNewCar: tempSendParams.value.isCurrentModel ? 2 : 1,
      groupForeignCar: getForeignCar(
        tempSendParams.value.isJapaneseCar,
        tempSendParams.value.isForeignCar
      ),
      groupDriveSystem: getDriveSystem(tempSendParams.value.driveTypes),
      groupMission: getMission(tempSendParams.value.mission),
    }
  }
)
const lengthAccordionFlg = useState<boolean>(
  'length_accordion_flg',
  () => false
)
const widthAccordionFlg = useState<boolean>('width_accordion_flg', () => false)
const heightAccordionFlg = useState<boolean>(
  'height_accordion_flg',
  () => false
)
const weightAccordionFlg = useState<boolean>(
  'weight_accordion_flg',
  () => false
)

const holdBodyTypeCheckList = useState<Array<Array<string>>>(
  'hold_body_types_check_list'
)

function getForeignCar(isJapaneseCar: boolean, isForeignCar: boolean) {
  if (!isJapaneseCar && !isForeignCar) {
    return 1
  } else if (isJapaneseCar) {
    return 2
  } else if (isForeignCar) {
    return 3
  }
  return 1
}

function getDriveSystem(value: string[]) {
  let driveSystem = 1
  value.forEach((driveType: string) => {
    if (driveType === 'FF') {
      driveSystem = 2
    } else if (driveType === '4WD') {
      driveSystem = 3
    }
  })
  return driveSystem
}

function getMission(value: string[]) {
  let mission = 1
  value.forEach((missionType: string) => {
    if (missionType === 'AT') {
      mission = 2
    } else if (missionType === 'MT') {
      mission = 3
    }
  })
  return mission
}

const checkboxSelectedClass: string = 'selected_checkbox'

const otherOptions: Ref<CheckedParamText[]> = ref([])
OTHER_OPTIONS.forEach((otherOption: IdName) => {
  otherOptions.value.push({
    param: otherOption.id,
    checked: false,
    text: otherOption.name,
  })
})

async function getModelList() {
  await nuxtApp.$getModelList(tempSendParams.value).then((result) => {
    if (result) {
      carModelList.value = result
      carCount.value = result.total_results_available
    } else {
      carCount.value = 0
    }
  })
}

function clearSendData() {
  groupConditionsSelected.value = {
    groupNewCar: 1,
    groupForeignCar: 1,
    groupDriveSystem: 1,
    groupMission: 1,
  }
  fuelCategories.value = []
  tempSendParams.value.bodyTypes = []
  tempSendParams.value.brands = []
  brandName.value = '指定なし'
  bodyTypeName.value = '指定なし'
  checkList.value = []
  for (let i = 0; i < bodyTypeLength.value; i++) {
    bodyTypeCheckList.value[i] = []
    allSelectFlgMap.value.set(i, false)
  }
  holdBodyTypeCheckList.value = _.cloneDeep(bodyTypeCheckList.value)
  holdAllSelectFlgMap.value = _.cloneDeep(allSelectFlgMap.value)
  catalogSearchMakerFlg.value = true
  catalogSearchBodyTypeFlg.value = true
  carLengthInputList.value = {}
  carWidthInputList.value = {}
  carHeightInputList.value = {}
  carWeightInputList.value = {}
  carLengthCheckList.value = []
  carWidthCheckList.value = []
  carHeightCheckList.value = []
  carWeightCheckList.value = []
  tempSendParams.value = nuxtApp.$createSearchParams()
}

function groupButtonChange(typeButton: string, positionButton: number) {
  // update state backgroud button selected
  switch (typeButton) {
    // 現行販売車
    case 'typeNewCar':
      if (positionButton === 1) {
        groupConditionsSelected.value.groupNewCar = 1
        tempSendParams.value.isCurrentModel = false
      } else if (positionButton === 2) {
        groupConditionsSelected.value.groupNewCar = 2
        tempSendParams.value.isCurrentModel = true
      }
      break
    // 国産車・輸入車
    case 'typeForeignCar':
      if (positionButton === 1) {
        groupConditionsSelected.value.groupForeignCar = 1
        tempSendParams.value.isJapaneseCar = false
        tempSendParams.value.isForeignCar = false
        // 指定なしの状態メーカーを選択できる
        makerLimitFlg.value = false
      } else if (positionButton === 2) {
        groupConditionsSelected.value.groupForeignCar = 2
        tempSendParams.value.isJapaneseCar = true
        tempSendParams.value.isForeignCar = false
        tempSendParams.value.brands = []
        checkList.value = []
        // 指定なしの状態メーカーを選択できない
        makerLimitFlg.value = true
      } else if (positionButton === 3) {
        groupConditionsSelected.value.groupForeignCar = 3
        tempSendParams.value.isJapaneseCar = false
        tempSendParams.value.isForeignCar = true
        tempSendParams.value.brands = []
        checkList.value = []
        // 指定なしの状態メーカーを選択できない
        makerLimitFlg.value = true
      }
      break
    // 駆動
    case 'typeDriveSystem':
      if (positionButton === 1) {
        groupConditionsSelected.value.groupDriveSystem = 1
        tempSendParams.value.driveTypes = []
      } else if (positionButton === 2) {
        groupConditionsSelected.value.groupDriveSystem = 2
        tempSendParams.value.driveTypes = ['FF', 'FR', 'RR', 'MR']
      } else if (positionButton === 3) {
        groupConditionsSelected.value.groupDriveSystem = 3
        tempSendParams.value.driveTypes = ['4WD', '6WD']
      }
      break
    // ミッション
    case 'typeMission':
      if (positionButton === 1) {
        groupConditionsSelected.value.groupMission = 1
        tempSendParams.value.mission = []
      } else if (positionButton === 2) {
        // MT以外
        groupConditionsSelected.value.groupMission = 2
        tempSendParams.value.mission = ['AT', 'CVT']
      } else if (positionButton === 3) {
        // MT
        groupConditionsSelected.value.groupMission = 3
        tempSendParams.value.mission = ['MT']
      }
      break

    default:
      break
  }
}

function finds() {
  if (!clickTag.value) {
    clickTag.value = true
    nuxtApp.$sendEventGaTag('CatalogPreferredConditionSearchTap')
    tempSendParams.value.transition_source = TRANSITION_SOURCE.condition_find
    const query = nuxtApp.$makeSearchConditions(tempSendParams.value)
    navigateTo({
      path: 'car-model-list',
      query,
    })
  }

  // call api search count
}

function changAccordionCheckBox(checkBoxArea: string) {
  if (checkBoxArea === 'length') {
    tempSendParams.value.carLength = carLengthCheckList.value
  } else if (checkBoxArea === 'width') {
    tempSendParams.value.width = carWidthCheckList.value
  } else if (checkBoxArea === 'height') {
    tempSendParams.value.height = carHeightCheckList.value
  } else if (checkBoxArea === 'weight') {
    tempSendParams.value.weight = carWeightCheckList.value
  }
}

function fixNumberSendParams(minMaxValue: string) {
  let resultValue: string = minMaxValue.replace(/[^0-9]/g, '').slice(0, 5)
  if (resultValue !== '') {
    resultValue = Number(resultValue).toString()
  }
  return resultValue
}

function changeInputArea(inputArea: string) {
  const submitValue: StringMinMax = {}
  if (inputArea === 'length') {
    if (carLengthInputList.value.max) {
      carLengthInputList.value.max = fixNumberSendParams(
        carLengthInputList.value.max
      )
      submitValue.max = carLengthInputList.value.max
    }
    if (carLengthInputList.value.min) {
      carLengthInputList.value.min = fixNumberSendParams(
        carLengthInputList.value.min
      )
      submitValue.min = carLengthInputList.value.min
    }
    tempSendParams.value.carLength = []
    if (submitValue.max || submitValue.min) {
      tempSendParams.value.carLength.push(submitValue)
    } else {
      tempSendParams.value.carLength = carLengthCheckList.value
    }
  } else if (inputArea === 'width') {
    if (carWidthInputList.value.max) {
      carWidthInputList.value.max = fixNumberSendParams(
        carWidthInputList.value.max
      )
      submitValue.max = carWidthInputList.value.max
    }
    if (carWidthInputList.value.min) {
      carWidthInputList.value.min = fixNumberSendParams(
        carWidthInputList.value.min
      )
      submitValue.min = carWidthInputList.value.min
    }
    tempSendParams.value.width = []
    if (submitValue.max || submitValue.min) {
      tempSendParams.value.width.push(submitValue)
    } else {
      tempSendParams.value.width = carWidthCheckList.value
    }
  } else if (inputArea === 'height') {
    if (carHeightInputList.value.max) {
      carHeightInputList.value.max = fixNumberSendParams(
        carHeightInputList.value.max
      )
      submitValue.max = carHeightInputList.value.max
    }
    if (carHeightInputList.value.min) {
      carHeightInputList.value.min = fixNumberSendParams(
        carHeightInputList.value.min
      )
      submitValue.min = carHeightInputList.value.min
    }
    tempSendParams.value.height = []
    if (submitValue.max || submitValue.min) {
      tempSendParams.value.height.push(submitValue)
    } else {
      tempSendParams.value.height = carHeightCheckList.value
    }
  } else if (inputArea === 'weight') {
    if (carWeightInputList.value.max) {
      carWeightInputList.value.max = fixNumberSendParams(
        carWeightInputList.value.max
      )
      submitValue.max = carWeightInputList.value.max
    }
    if (carWeightInputList.value.min) {
      carWeightInputList.value.min = fixNumberSendParams(
        carWeightInputList.value.min
      )
      submitValue.min = carWeightInputList.value.min
    }
    tempSendParams.value.weight = []
    if (submitValue.max || submitValue.min) {
      tempSendParams.value.weight.push(submitValue)
    } else {
      tempSendParams.value.weight = carWeightCheckList.value
    }
  }
}

function changFuelCategories() {
  const fuelCds: number[] = []
  fuelCategories.value.forEach((fuelCategory) => {
    switch (fuelCategory) {
      case 'ガソリン':
        fuelCds.push(7, 8)
        break
      case '軽油':
        fuelCds.push(6)
        break
      case '電気自動車(EV)':
        fuelCds.push(9)
        break
      case 'ハイブリッド':
        fuelCds.push(1, 4, 10, 15)
        break
      case 'プラグイン ハイブリッド':
        fuelCds.push(11, 12, 16)
        break
      case 'LPG車':
        fuelCds.push(5)
        break
      case 'CNG車':
        fuelCds.push(2)
        break
      case '水素':
        fuelCds.push(14)
        break
      default:
        break
    }
  })
  tempSendParams.value.fuelCds = fuelCds
}

function createDisplayInput(text: string) {
  return text.length > 21 ? text.slice(0, 21) + '•••' : text
}

function initialSetParams() {
  brandName.value = '指定なし'
  bodyTypeName.value = '指定なし'
  tagMakerFlg.value = true
  fuelCategories.value = []

  if (tempSendParams.value.isCurrentModel) {
    groupConditionsSelected.value.groupNewCar = 2
  }

  if (
    !tempSendParams.value.isJapaneseCar &&
    !tempSendParams.value.isForeignCar
  ) {
    groupConditionsSelected.value.groupForeignCar = 1
    makerLimitFlg.value = false
  } else if (tempSendParams.value.isJapaneseCar) {
    groupConditionsSelected.value.groupForeignCar = 2
    makerLimitFlg.value = true
  } else if (tempSendParams.value.isForeignCar) {
    groupConditionsSelected.value.groupForeignCar = 3
    makerLimitFlg.value = true
  }

  if (tempSendParams.value.brands.length > 0) {
    groupButtonChange('typeForeignCar', 1)
    tagMakerFlg.value = false
    const brandNames: string[] = []
    tempSendParams.value.brands.forEach((v) => {
      const brand = brandList.find((e) => e.id === v)
      if (brand) {
        brandNames.push(brand.name)
      }
    })
    brandName.value = createDisplayInput(brandNames.join(','))
  }

  if (tempSendParams.value.bodyTypes.length > 0) {
    const bodyTypeNames: string[] = []
    tempSendParams.value.bodyTypes.forEach((v) => {
      const bodyType = bodyTypeList.find((e) => e.id === v)
      if (bodyType) {
        bodyTypeNames.push(bodyType.name)
      }
    })
    bodyTypeName.value = createDisplayInput(bodyTypeNames.join(','))
  }

  if (
    tempSendParams.value.driveTypes &&
    tempSendParams.value.driveTypes.length > 0
  ) {
    tempSendParams.value.driveTypes.forEach((driveType: string) => {
      if (driveType === 'FF') {
        groupConditionsSelected.value.groupDriveSystem = 2
      } else if (driveType === '4WD') {
        groupConditionsSelected.value.groupDriveSystem = 3
      }
    })
  }
  if (tempSendParams.value.fuelCds.length > 0) {
    fuelCategories.value = []
    tempSendParams.value.fuelCds.forEach((fuelCd) => {
      switch (fuelCd) {
        case 1: // 選択時含む値を探す
          fuelCategories.value.push('ハイブリッド')
          break
        case 2:
          fuelCategories.value.push('CNG車')
          break
        case 5:
          fuelCategories.value.push('LPG車')
          break
        case 6:
          fuelCategories.value.push('軽油')
          break
        case 7:
          fuelCategories.value.push('ガソリン')
          break
        case 9:
          fuelCategories.value.push('電気自動車(EV)')
          break
        case 11:
          fuelCategories.value.push('プラグイン ハイブリッド')
          break
        case 14:
          fuelCategories.value.push('水素')
          break
        default:
          break
      }
    })
  }
  if (tempSendParams.value.mission.length > 0) {
    tempSendParams.value.mission.forEach((mission) => {
      if (mission === 'AT') {
        groupConditionsSelected.value.groupMission = 2
      } else if (mission === 'MT') {
        groupConditionsSelected.value.groupMission = 3
      }
    })
  }
}

function initialSet() {
  groupConditionsSelected.value.groupNewCar = tempSendParams.value
    .isCurrentModel
    ? 2
    : 1
  groupConditionsSelected.value.groupForeignCar = getForeignCar(
    tempSendParams.value.isJapaneseCar,
    tempSendParams.value.isForeignCar
  )
  groupConditionsSelected.value.groupDriveSystem = getDriveSystem(
    tempSendParams.value.driveTypes
  )
  groupConditionsSelected.value.groupMission = getMission(
    tempSendParams.value.mission
  )
  if (newCar1.value && groupConditionsSelected.value.groupNewCar === 1) {
    newCar1.value.checked = true
  }
  if (newCar2.value && groupConditionsSelected.value.groupNewCar === 2) {
    newCar2.value.checked = true
  }
  if (
    foreignCar1.value &&
    groupConditionsSelected.value.groupForeignCar === 1
  ) {
    foreignCar1.value.checked = true
  }
  if (
    foreignCar2.value &&
    groupConditionsSelected.value.groupForeignCar === 2
  ) {
    foreignCar2.value.checked = true
  }
  if (
    foreignCar3.value &&
    groupConditionsSelected.value.groupForeignCar === 3
  ) {
    foreignCar3.value.checked = true
  }
  if (
    driveSystem1.value &&
    groupConditionsSelected.value.groupDriveSystem === 1
  ) {
    driveSystem1.value.checked = true
  }
  if (
    driveSystem2.value &&
    groupConditionsSelected.value.groupDriveSystem === 2
  ) {
    driveSystem2.value.checked = true
  }
  if (
    driveSystem3.value &&
    groupConditionsSelected.value.groupDriveSystem === 3
  ) {
    driveSystem3.value.checked = true
  }
  if (mission1.value && groupConditionsSelected.value.groupMission === 1) {
    mission1.value.checked = true
  }
  if (mission2.value && groupConditionsSelected.value.groupMission === 2) {
    mission2.value.checked = true
  }
  if (mission3.value && groupConditionsSelected.value.groupMission === 3) {
    mission3.value.checked = true
  }
}

function routeMakerBodyType(path: string) {
  if (!clickTag.value) {
    clickTag.value = true
    navigateTo({
      path: path,
      query: { transition_source: TRANSITION_SOURCE.condition_find },
    })
  }
}

function tapAccordion(titleCategory: string) {
  if (titleCategory === 'length') {
    lengthAccordionFlg.value = !lengthAccordionFlg.value
  } else if (titleCategory === 'width') {
    widthAccordionFlg.value = !widthAccordionFlg.value
  } else if (titleCategory === 'height') {
    heightAccordionFlg.value = !heightAccordionFlg.value
  } else if (titleCategory === 'weight') {
    weightAccordionFlg.value = !weightAccordionFlg.value
  }
}

/**
 * tagMakerFlg true国産車・輸入車タグが選択できる
 * 転送値あるならbrandNameとbodyTypeNameに入れる
 * メーカーを選択したら、tagMakerFlgをfalseにし国産車・輸入車タグは選択できない
 */
watch(
  () => tempSendParams.value,
  () => {
    clearTimeout(timer.value)
    timer.value = setTimeout(function () {
      getModelList()
    }, 500)
    initialSetParams()
  },
  { deep: true }
)

onMounted(() => {
  initialSetParams()
  initialSet()
  clickTag.value = false

  if (catalogSearchMakerFlg.value) {
    tempSendParams.value.brands = []
    brandName.value = '指定なし'
    checkList.value = []
  }
  if (catalogSearchBodyTypeFlg.value) {
    tempSendParams.value.bodyTypes = []
    bodyTypeName.value = '指定なし'
    for (let i = 0; i < bodyTypeLength.value; i++) {
      bodyTypeCheckList.value[i] = []
      allSelectFlgMap.value.set(i, false)
    }
    holdBodyTypeCheckList.value = _.cloneDeep(bodyTypeCheckList.value)
    holdAllSelectFlgMap.value = _.cloneDeep(allSelectFlgMap.value)
  }
  if (!catalogSearchMakerFlg.value && !catalogSearchBodyTypeFlg.value) {
    getModelList()
  }
})

onUnmounted(() => {
  clearTimeout(timer.value)
})
</script>

<template>
  <div class="item-group">
    <div class="item_title">新車時価格</div>
    <div class="select-2col">
      <div class="select">
        <div class="selectWrap select-2col">
          <select
            v-model="tempSendParams.newCarPrice.min"
            name="select_body"
            class="select_body"
          >
            <option
              v-for="newcarprice in CAR_PRICE"
              :key="newcarprice"
              :value="newcarprice"
            >
              {{
                newcarprice === -1
                  ? '下限なし'
                  : newcarprice.toLocaleString() + '万円'
              }}
            </option>
          </select>
        </div>
      </div>
      <span>～</span>
      <div class="select">
        <div class="selectWrap select-2col">
          <select
            v-model="tempSendParams.newCarPrice.max"
            name="select_body"
            class="select_body"
          >
            <option
              v-for="newcarprice in CAR_PRICE"
              :key="newcarprice"
              :value="newcarprice"
            >
              {{
                newcarprice === -1
                  ? '上限なし'
                  : newcarprice.toLocaleString() + '万円'
              }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">現行販売車</div>
    <div class="radio-btn-group" role="group">
      <label class="radio-btn">
        <input
          ref="newCar1"
          v-model="groupConditionsSelected.groupNewCar"
          :value="1"
          class="radio-btn-box"
          type="radio"
          name="newCar"
          @click="groupButtonChange('typeNewCar', 1)"
        /><span class="radio-button-text">指定なし</span>
      </label>
      <label>
        <input
          ref="newCar2"
          v-model="groupConditionsSelected.groupNewCar"
          :value="2"
          class="radio-btn-box"
          type="radio"
          name="newCar"
          @click="groupButtonChange('typeNewCar', 2)"
        /><span class="radio-button-text">現行販売車</span>
      </label>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">国産車・輸入車</div>
    <div class="radio-btn-group" role="group">
      <label class="radio-btn">
        <input
          ref="foreignCar1"
          v-model="groupConditionsSelected.groupForeignCar"
          :value="1"
          type="radio"
          class="radio-btn-box"
          name="foreignCar"
          @click="groupButtonChange('typeForeignCar', 1)"
        /><span class="radio-button-text">指定なし</span>
      </label>
      <label class="radio-btn foreign-car">
        <input
          ref="foreignCar2"
          v-model="groupConditionsSelected.groupForeignCar"
          :value="2"
          type="radio"
          class="radio-btn-box"
          name="foreignCar"
          @click="groupButtonChange('typeForeignCar', 2)"
        />
        <span class="radio-button-text">国産車<br />すべて</span>
      </label>
      <label class="radio-btn foreign-car">
        <input
          ref="foreignCar3"
          v-model="groupConditionsSelected.groupForeignCar"
          :value="3"
          type="radio"
          class="radio-btn-box"
          name="foreignCar"
          @click="groupButtonChange('typeForeignCar', 3)"
        />
        <span class="radio-button-text">輸入車<br />すべて</span>
      </label>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">メーカー</div>
    <div class="select">
      <div
        class="underscore-link"
        @click="routeMakerBodyType('brand-selection')"
      >
        <div class="select_1col" :class="makerLimitFlg ? '' : 'selectWrap'">
          <input
            v-if="!makerLimitFlg"
            type="text"
            class="select_body brand-area"
            name="brandSelection"
            :value="brandName"
            readonly
          />

          <input
            v-if="makerLimitFlg"
            type="text"
            class="select_body brand-area"
            name="brandSelection"
            :value="brandName"
            style="color: grey"
            disabled
          />
        </div>
      </div>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">ボディタイプ</div>
    <div class="select">
      <div
        class="underscore-link"
        @click="routeMakerBodyType('body-type-selection')"
      >
        <div class="selectWrap select_1col">
          <input
            type="text"
            class="select_body brand-area"
            name="bodyTypeSelection"
            :value="bodyTypeName"
            readonly
          />
        </div>
      </div>
    </div>
  </div>
  <div class="item-group">
    <div class="item_title title-border">サイズ</div>
    <div class="size-title" @click="tapAccordion('length')">
      <div class="size-title-name">全長を選択する</div>
      <div class="plus-icon">
        {{ lengthAccordionFlg ? 'ー' : '＋' }}
      </div>
    </div>
    <div v-show="lengthAccordionFlg">
      <div v-for="length in CAR_LENGTH" :key="length.name">
        <label
          class="checkbox-area"
          :class="[
            carLengthInputList.min || carLengthInputList.max
              ? 'disable-color'
              : '',
          ]"
        >
          <input
            v-model="carLengthCheckList"
            type="checkbox"
            class="size-checkbox form-check-input"
            :value="length.value"
            :disabled="
              carLengthInputList.min || carLengthInputList.max ? true : false
            "
            @change="changAccordionCheckBox('length')"
          />
          <div class="size-content">{{ length.name }}</div>
        </label>
      </div>
      <div class="free-input-title">全長を指定する</div>
      <div class="free-input-area">
        <div class="input-caset">
          <input
            v-model="carLengthInputList.min"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="下限なし"
            @input="changeInputArea('length')"
          />
          <span class="middle-icon">～</span>
          <input
            v-model="carLengthInputList.max"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="上限なし"
            @input="changeInputArea('length')"
          />
        </div>
        <div class="unit-mm">mm</div>
      </div>
    </div>

    <div class="size-title" @click="tapAccordion('width')">
      <div class="size-title-name">全幅を選択する</div>
      <div class="plus-icon">
        {{ widthAccordionFlg ? 'ー' : '＋' }}
      </div>
    </div>
    <div v-show="widthAccordionFlg">
      <div v-for="width in CAR_WIDTH" :key="width.name">
        <label
          class="checkbox-area"
          :class="[
            carWidthInputList.max || carWidthInputList.min
              ? 'disable-color'
              : '',
          ]"
        >
          <input
            v-model="carWidthCheckList"
            type="checkbox"
            class="size-checkbox form-check-input"
            :value="width.value"
            :disabled="
              carWidthInputList.max || carWidthInputList.min ? true : false
            "
            @change="changAccordionCheckBox('width')"
          />
          <div class="size-content">{{ width.name }}</div>
        </label>
      </div>
      <div class="free-input-title">全幅を指定する</div>
      <div class="free-input-area">
        <div class="input-caset">
          <input
            v-model="carWidthInputList.min"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="下限なし"
            @input="changeInputArea('width')"
          />
          <span class="middle-icon">～</span>
          <input
            v-model="carWidthInputList.max"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="上限なし"
            @input="changeInputArea('width')"
          />
        </div>
        <div class="unit-mm">mm</div>
      </div>
    </div>

    <div class="size-title" @click="tapAccordion('height')">
      <div class="size-title-name">全高を選択する</div>
      <div class="plus-icon">
        {{ heightAccordionFlg ? 'ー' : '＋' }}
      </div>
    </div>
    <div v-show="heightAccordionFlg">
      <div v-for="height in CAR_HEIGHT" :key="height.name">
        <label
          class="checkbox-area"
          :class="[
            carHeightInputList.max || carHeightInputList.min
              ? 'disable-color'
              : '',
          ]"
        >
          <input
            v-model="carHeightCheckList"
            type="checkbox"
            class="size-checkbox form-check-input"
            :value="height.value"
            :disabled="
              carHeightInputList.max || carHeightInputList.min ? true : false
            "
            @change="changAccordionCheckBox('height')"
          />
          <div class="size-content">{{ height.name }}</div>
        </label>
      </div>
      <div class="free-input-title">全高を指定する</div>
      <div class="free-input-area">
        <div class="input-caset">
          <input
            v-model="carHeightInputList.min"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="下限なし"
            @input="changeInputArea('height')"
          />
          <span class="middle-icon">～</span>
          <input
            v-model="carHeightInputList.max"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="上限なし"
            @input="changeInputArea('height')"
          />
        </div>
        <div class="unit-mm">mm</div>
      </div>
    </div>
    <div class="attention">
      <span data-bs-toggle="modal" data-bs-target="#modalCarSize"
        >車体サイズの入力について</span
      >
    </div>
  </div>

  <div class="item-group item-group-weight">
    <div class="item_title title-border">車両重量</div>
    <div class="size-title" @click="tapAccordion('weight')">
      <div class="size-title-name">車両重量を選択する</div>
      <div class="plus-icon">
        {{ weightAccordionFlg ? 'ー' : '＋' }}
      </div>
    </div>
    <div v-show="weightAccordionFlg">
      <div v-for="weight in CAR_WEIGHT" :key="weight.name">
        <label
          class="checkbox-area"
          :class="[
            carWeightInputList.max || carWeightInputList.min
              ? 'disable-color'
              : '',
          ]"
        >
          <input
            v-model="carWeightCheckList"
            type="checkbox"
            class="size-checkbox form-check-input"
            :value="weight.value"
            :disabled="
              carWeightInputList.max || carWeightInputList.min ? true : false
            "
            @change="changAccordionCheckBox('weight')"
          />
          <div class="size-content">{{ weight.name }}</div>
        </label>
      </div>
      <div class="free-input-title">車両重量を指定する</div>
      <div class="free-input-area">
        <div class="input-caset">
          <input
            v-model="carWeightInputList.min"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="下限なし"
            @input="changeInputArea('weight')"
          />
          <span class="middle-icon">～</span>
          <input
            v-model="carWeightInputList.max"
            type="text"
            inputmode="numeric"
            class="input-area"
            placeholder="上限なし"
            @input="changeInputArea('weight')"
          />
        </div>
        <div class="unit-mm">kg</div>
      </div>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">乗車定員</div>
    <div class="select-2col">
      <div class="select">
        <div class="selectWrap select_1col">
          <select
            v-model="tempSendParams.ridingCapacity.min"
            name="select_body"
            class="select_body"
          >
            <option value="0">下限なし</option>
            <option v-for="n in 16" :key="n" :value="n">{{ n }}人</option>
          </select>
        </div>
      </div>
      <span>～</span>
      <div class="select">
        <div class="selectWrap select_1col">
          <select
            v-model="tempSendParams.ridingCapacity.max"
            name="select_body"
            class="select_body"
          >
            <option value="0">上限なし</option>
            <option v-for="n in 16" :key="n" :value="n">{{ n }}人</option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">駆動</div>
    <div class="radio-btn-group" role="group">
      <label class="radio-btn">
        <input
          ref="driveSystem1"
          v-model="groupConditionsSelected.groupDriveSystem"
          :value="1"
          type="radio"
          class="radio-btn-box"
          name="driveSystem"
          @click="groupButtonChange('typeDriveSystem', 1)"
        /><span class="radio-button-text">指定なし</span>
      </label>
      <label class="radio-btn">
        <input
          ref="driveSystem2"
          v-model="groupConditionsSelected.groupDriveSystem"
          :value="2"
          type="radio"
          class="radio-btn-box"
          name="driveSystem"
          @click="groupButtonChange('typeDriveSystem', 2)"
        /><span class="radio-button-text">2WD</span>
      </label>
      <label class="radio-btn">
        <input
          ref="driveSystem3"
          v-model="groupConditionsSelected.groupDriveSystem"
          :value="3"
          type="radio"
          class="radio-btn-box"
          name="driveSystem"
          @click="groupButtonChange('typeDriveSystem', 3)"
        /><span class="radio-button-text">4WD</span>
      </label>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">ミッション</div>
    <div class="radio-btn-group" role="group">
      <label class="radio-btn">
        <input
          ref="mission1"
          v-model="groupConditionsSelected.groupMission"
          :value="1"
          type="radio"
          class="radio-btn-box"
          name="mission"
          @click="groupButtonChange('typeMission', 1)"
        /><span class="radio-button-text">指定なし</span>
      </label>
      <label class="radio-btn">
        <input
          ref="mission2"
          v-model="groupConditionsSelected.groupMission"
          :value="2"
          type="radio"
          class="radio-btn-box"
          name="mission"
          @click="groupButtonChange('typeMission', 2)"
        /><span class="radio-button-text">AT/CVT</span>
      </label>
      <label class="radio-btn">
        <input
          ref="mission3"
          v-model="groupConditionsSelected.groupMission"
          :value="3"
          type="radio"
          class="radio-btn-box"
          name="mission"
          @click="groupButtonChange('typeMission', 3)"
        /><span class="radio-button-text">MT</span>
      </label>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">燃料</div>
    <div class="checkbox">
      <div class="container">
        <div class="row">
          <div v-for="fuel in FUELS" :key="fuel.id" class="col-6 check-col6">
            <label
              class="list-group-item check-item"
              :class="[
                fuelCategories.find((el: string) => el === fuel.name)
                  ? checkboxSelectedClass
                  : '',
              ]"
              @focus="true"
            >
              <input
                v-model="fuelCategories"
                class="form-check-input me-2"
                name="fuel"
                type="checkbox"
                :value="fuel.name"
                @change="changFuelCategories()"
              />
              <span>{{ fuel.name }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="item-group">
    <div class="item_title">その他</div>
    <div class="checkbox">
      <div class="container">
        <div class="row">
          <div
            v-for="other in OTHER_OPTIONS"
            :key="other.id"
            class="col-6 check-col6"
          >
            <label
              class="list-group-item check-item"
              :class="[tempSendParams.isSpecial ? checkboxSelectedClass : '']"
            >
              <input
                v-model="tempSendParams.isSpecial"
                class="form-check-input me-2"
                name="other"
                type="checkbox"
              />
              <span>{{ other.name }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="container action_layout">
    <div class="row">
      <div class="col-4">
        <button
          type="button"
          class="btn btn-secondary btnClear"
          @click="clearSendData()"
        >
          <span class="clear-str">クリア</span>
        </button>
      </div>
      <div class="col-8">
        <button
          v-if="carCount !== 0"
          type="button"
          class="btn btn-primary btnSearch"
          @click="finds"
        >
          {{ carCount.toLocaleString() }}
          <span class="sw-1">件</span><span class="sw-2">検索する</span>
        </button>
        <button
          v-else
          type="button"
          class="btn btn-primary btnDisabled"
          disabled
        >
          {{ carCount.toLocaleString() }}
          <span class="sw-1">件</span><span class="sw-2">検索する</span>
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped>
.radio-btn-group {
  display: flex;
  align-items: center;
  width: 100%;
}

.radio-btn {
  width: 33.3%;
}
.fuel-efficiency {
  width: 33.3%;
  white-space: nowrap;
}
.foreign-car {
  display: flex;
  line-height: 1;
}

.radio-btn-box {
  appearance: none;
  position: absolute;
}

.radio-button-text::before {
  content: '';
  display: block;
  margin-right: 8px;
  border-radius: 50%;
  border: 2px solid #d5d5d5;
  width: 24px;
  height: 24px;
}

.radio-btn-box:checked + .radio-button-text::before {
  content: '';
  display: block;
  margin-right: 8px;
  border-radius: 50%;
  border: 3px solid #de2020;
  width: 24px;
  height: 24px;
}

.radio-btn-box:checked + .radio-button-text::after {
  position: absolute;
  left: 6px;
  content: '';
  display: block;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  background-color: #de2020;
}

.radio-button-text {
  position: relative;
  display: flex;
  align-items: center;
}

.brand-area {
  width: 100%;
  padding: 10px;
  background-color: #ffffff;
  border-radius: 2px;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border: 1px solid #d5d5d5;
}

.item-group {
  margin-top: 24px;
}

.item-group-weight {
  margin-top: 19px !important;
}

.item_title {
  color: #333333;
  font-size: 14px;
  font-weight: bold;
  padding-bottom: 14px;
}

.title-border {
  border-bottom: 1px solid #ecf0f2;
}

.size-title {
  padding: 16px 17px 16px 12px;
  font-size: 14px;
  border-bottom: 1px solid #ecf0f2;
  display: flex;
  justify-content: space-between;
}

.size-title-name {
  display: flex;
  align-items: center;
}

.size-content {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.plus-icon {
  font-weight: bold;
  min-height: 14px;
  min-width: 14px;
}

.checkbox-area {
  display: flex;
  padding: 17px 16px 16px 17px;
  border-bottom: 1px solid #ecf0f2;
  align-items: center;
}

.disable-color {
  opacity: 50%;
}

.free-input-title {
  padding: 17px 16px 16px 17px;
}

.middle-icon {
  font-size: 14px;
  font-weight: bold;
  margin: 0px 6px;
}

.size-checkbox {
  min-width: 18px;
  min-height: 18px;
  margin-right: 9px;
  margin-top: 0px;
}

.select-2col {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-direction: row;
}

.free-input-area {
  display: flex;
  padding-left: 17px;
  padding-right: 17px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ecf0f2;
}

.input-caset {
  display: flex;
  align-items: center;
}

.input-caset .input-area {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #d5d5d5;
  border-radius: 2px;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  padding: 10px;
  min-height: 44px;
  background-color: #fff;
  color: #000000e5;
}

.unit-mm {
  display: flex;
  align-items: flex-end;
  font-size: 12px;
  padding-left: 4px;
}

.select-2col span {
  font-size: 14px;
  font-weight: bold;
  margin: 5px 5px 10px 5px;
}

.height-middle {
  margin: 23px 5px 0px 5px !important;
}

.select-2col .selectWrap::after {
  transform: rotate(90deg);
  -webkit-transform: rotate(90deg);
}

.select-1col .selectWrap::after {
  transform: rotate(90deg);
  -webkit-transform: rotate(90deg);
}

.select {
  padding: 0;
  text-align: left;
  font-size: 14px;
  width: 100%;
}

.height-area {
  align-items: start;
}

.select_1col {
  position: relative;
  width: 100%;
  height: auto;
}

.select select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #d5d5d5;
  border-radius: 2px;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  padding: 10px;
  min-width: 100%;
  min-height: 44px;
  background-color: #fff;
  color: #000000e5;
}

.select .input-area {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #d5d5d5;
  border-radius: 2px;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  padding: 10px;
  width: 83%;
  min-height: 44px;
  background-color: #fff;
  color: #000000e5;
}

input::placeholder {
  color: #000000e5;
}

.min-max-area {
  display: flex;
  width: 100%;
}

.attention {
  font-size: 12px;
  margin-top: 15px;
  color: #1a73e8;
  width: 100%;
  text-align: right;
}

.select-2col {
  width: 100%;
  position: relative;
}

.select-2col .select select {
  display: flex;
  width: 100%;
}

.input-area {
  width: 100%;
}
.select .select_body {
  display: flex;
  color: #000000e5;
}

.selectWrap::after {
  pointer-events: none;
  background: url('@/assets/image/icon/arrow_right.svg') center center no-repeat;
  background-size: 18px;
  width: 18px;
  margin: auto;
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 10px;
}

.checkbox {
  width: 100%;
}

.item-group .check-col6 {
  padding-left: 0;
  padding-right: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid #0000001a;
}

.item-group .check-col6:nth-of-type(odd) {
  border-right: 1px solid #0000001a;
}

.item-group .check-col6:nth-child(1),
.item-group .check-col6:nth-child(2) {
  border-top: 1px solid #0000001a;
}

.checkbox .check-item {
  font-size: 14px;
  padding: 10px 20px 10px 20px;
  color: #000000;
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 50px;
  height: 100%;
  min-width: 100%;
}

.selected_checkbox {
  background-color: #ffe5e6;
}

.checkbox .check-item .form-check-input {
  margin-top: 0px !important;
  min-width: 18px;
  min-height: 18px;
}

.form-check-input:checked {
  background-color: #df2020;
  border-color: #df2020;
}

input[type='checkbox'] {
  vertical-align: middle;
  border: 1px solid #d5d5d5;
}

.btnNewCar {
  margin-top: 30px;
  background: #ffffff;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ededed;
}

.btnNewCar a {
  text-decoration: none;
  color: #333333;
}

.btnNewCar img {
  font-size: 15px;
  margin-top: 5px;
}

.tags {
  margin-top: 20px;
}

.tags .tag {
  border: 1px solid #ededed;
  border-radius: 15px;
  padding: 5px;
  margin-right: 20px;
  line-height: 2.5;
  white-space: nowrap;
}

.tags .tag:active {
  background: red;
  color: white;
}

.action_layout {
  margin-top: 20px;
  margin-bottom: 20px;
}

.action_layout .btn {
  opacity: 100%;
  border-radius: 10px;
  min-height: 42px;
  border: none;
}

.action_layout .btnClear {
  margin-left: calc(-12px);
  min-width: calc(100vw / 4);
  margin-right: 10px;
  background: #e5e5e5;
  color: #000000;
}

.action_layout .btnSearch {
  margin-left: calc(-12px);
  min-width: calc(100vw / 1.78);
  background: #df2020;
  color: #ffffff;
  font-size: 16px;
}
.action_layout .btnDisabled {
  margin-left: calc(-12px);
  min-width: calc(100vw / 1.78);
  background: #908e8e;
  color: #ffffff;
  font-size: 16px;
}
.clear-str {
  font-size: 14px;
}

.sw-1 {
  font-size: 12px;
}
.sw-2 {
  font-size: 14px;
  margin-left: 5px;
}
</style>
